/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
 import React, { Fragment } from "react";
 import styled from "@mui/styled-engine-sc";
 import { useTheme } from "@mui/material";
 import useMediaQuery from "@mui/material/useMediaQuery";
 import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext } from "pure-react-carousel";
 import "pure-react-carousel/dist/react-carousel.es.css";
 import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
 import ChevronRightIcon from "@mui/icons-material/ChevronRight";
 import { StyledGrid, StyledTypography } from "@hcl-commerce-store-sdk/react-component";
 
 const StyledCarouselProvider = styled(({ ...props }) => <CarouselProvider {...props} />)`
   ${({ theme }) => `
    position: relative;
    padding: 0px 0px 0px 0px;
    margin-top: ${theme.spacing(3)};
    margin-bottom: ${theme.spacing(3)};
    height: auto;
  
    ${theme.breakpoints.down(410)} {
      .carousel__slider.carousel__slider--horizontal {
        height: inherit;
        > .carousel__slider-tray-wrapper.carousel__slider-tray-wrap--horizontal {
          height: inherit;
          > .carousel__slider-tray.carousel__slider-tray--horizontal {
            height: inherit;
            > .carousel__slide {
              height: inherit;
            }
          }
        }
      }
    }
    
    .carousel__back-button-icon,
    .carousel__next-button-icon {
      display: flex;
      position: relative;
      height: 38px;
      width: 38px;
      border: 1px solid;
      border-color: ${theme.palette.primary.main};
      border-radius: 50%;
      background: none;
      justify-content: center;
      align-items: center;

      &:hover {
        cursor: pointer;
        color: ${theme.palette.primary.main};
        border-color: ${theme.palette.primary.main};
      }
    }

    .general-title-buttons-container{
      padding-bottom: 14px;
    }

    .carousel-title-container {
      padding: 0px 0px 0px 21px;
    }

    .carousel-buttons-container {
      padding: 0px 0px 0px 19px;
    }

    .button-next-back {
      padding: 4.5px;
    }
  
    .carousel-title {
      font-family: Inter;
      font-variation-settings: 'wght' 600;
      color: ${theme.palette.primary.main};
    }
  
    div {
      outline: none;
    }
    
   `}
 `;
 
 const StyledSlide = styled(({ ...props }) => <Slide {...props} />)`
   ${({ theme }) => `
     text-align: center;
     min-height: 400px;
     overflow: hidden;
 
     .product-card {
       max-width: 260px;
       display: inline-block;

       ${theme.breakpoints.down(410)} {
         min-width: 195px;
         min-height: 400px;
       }
       ${theme.breakpoints.up(410)} {
         min-width: 225px;
         min-height: 400px;
       }
     }
   `}
 `;
 
 const StyledStaticSlideList = styled("div")`
   ${({ theme }) => `
     margin-top: ${theme.spacing(3)};
 
     display: flex;
     justify-content: center;
     align-items: stretch;
 
     .product-card {
       max-width: 245px;
       min-width: 225px;
       display: inline-block;
       max-height: 380px;
       margin-right: ${theme.spacing(3)};
     }
   `}
 `;
 
 interface StyledProductRecommendationSliderProps {
   slidesList: any,
   carouselTitle: string | undefined
 }
 
 const StyledProductRecommendationSlider = (prop: StyledProductRecommendationSliderProps) => {
   const theme = useTheme();
   
   let slidesShown = 5;
   let disabledSliding = false;
 
   if (useMediaQuery(theme.breakpoints.down("lg"))) {
     slidesShown = 4;
   }
 
   if (useMediaQuery(theme.breakpoints.down("md"))) {
     slidesShown = 3;
   }
 
   if (useMediaQuery(theme.breakpoints.down("sm"))) {
     slidesShown = 1;
   }
 
   // Check if there are enough slides to cycle through
   if (slidesShown >= prop.slidesList.length) {
     disabledSliding = true;
   }
 
   return disabledSliding ? (
     <StyledStaticSlideList>
       {prop.slidesList.map((slide: any, i: number) => (
         <Fragment key={i}>{slide}</Fragment>
       ))}
     </StyledStaticSlideList>
   ) : (
     <StyledCarouselProvider
       naturalSlideWidth={230}
       naturalSlideHeight={300}
       visibleSlides={slidesShown}
       step={slidesShown}
       infinite={true}
       dragEnabled={false}
       totalSlides={prop.slidesList.length}>
         
        <StyledGrid className="general-title-buttons-container" container alignItems="center" spacing={2}>

          <StyledGrid item container className="carousel-title-container" xs={6} justifyContent="flex-start">
            <StyledGrid item>
              <StyledTypography className="carousel-title" variant="h4">{prop.carouselTitle}</StyledTypography>
            </StyledGrid>
          </StyledGrid>

          <StyledGrid item container className="carousel-buttons-container" xs={6} justifyContent="flex-end">
            <StyledGrid item className="button-next-back ">
              <ButtonBack className="carousel__back-button-icon">
                <ChevronLeftIcon />
              </ButtonBack>
            </StyledGrid>

            <StyledGrid item className="button-next-back ">
              <ButtonNext className="carousel__next-button-icon">
                <ChevronRightIcon />
              </ButtonNext>
            </StyledGrid>
          </StyledGrid>

        </StyledGrid>
 
        <Slider>
          {prop.slidesList.map((slide: any, i: number) => (
            <StyledSlide index={i} key={i}>
              {slide}
            </StyledSlide>
          ))}
        </Slider>
       
     </StyledCarouselProvider>
   );
 };
 
 export { StyledProductRecommendationSlider };
 